import { PageProps, graphql } from 'gatsby';
import React, { useMemo } from 'react';
import Layout from '../components/layouts/Layout';
import AccessoryDetailsScreen from '../components/screens/AccessoryDetailsScreen/AccessoryDetailsScreen';
import { StoreLocatorProvider } from '../components/StoreLocator';
import { getActiveAccessoryVariants } from '../utils/activeAccessories';
import { GatsbyPageContextProps } from '../types';

const AccessoryDetailsPage = ({
  pageContext,
  data,
  location,
}: PageProps<Record<string, any>, GatsbyPageContextProps>) => {
  const { locale } = pageContext;
  const activeVariants = useMemo(() => getActiveAccessoryVariants(
    data.contentfulAccessory?.variants,
  ), [data.contentfulAccessory?.variants]);
  const contentfulData = useMemo(() => {
    if (data.contentfulAccessory?.isActive) {
      return {
        ...data.contentfulAccessory,
        variants: activeVariants,
      };
    }
    return undefined;
  }, [data.contentfulAccessory, activeVariants]);
  return (
    <StoreLocatorProvider>
      <Layout locale={locale} location={location}>
        {contentfulData && (
        <AccessoryDetailsScreen
          contentfulData={
            contentfulData
          }
          productDetailsPageData={data.productDetailsPage}
        />
        )}
      </Layout>
    </StoreLocatorProvider>
  );
};

// ts-prune-ignore-next
export const query = graphql`
  query AccessoryDetails(
    $formattedLocale: String = "en-CA"
    $contentful_id: String!
  ) {
    productDetailsPage: contentfulPage(
      contentful_id: { eq: "3wfNyHwlZgGrmd5Iqd726p" }
      node_locale: { eq: $formattedLocale }
    ) {
      metaDescription {
        metaDescription
      }
      pageSections {
        ... on ContentfulPhoneInventoryThreshold {
          __typename
          contentful_id
          threshold
          inventoryStatus {
            inventoryStatusCode
            inventoryStatusIcon {
              asset {
                url
                localImage {
                  id
                  publicURL
                  childImageSharp {
                    gatsbyImageData
                  }
                }
              }
            }
            inventoryStatusMessage {
              inventoryStatusMessage
            }
          }

          storeNotSelectedMessage {
            storeNotSelectedMessage
          }
          carrierNotSelectedMessage {
            carrierNotSelectedMessage
          }

          storeNotSelectedIcon {
            asset {
              url
              localImage {
                id
                publicURL
                childImageSharp {
                  gatsbyImageData
                }
              }
            }
          }
          carrierNotSelectedIcon {
            asset {
              url
              localImage {
                id
                publicURL
                childImageSharp {
                  gatsbyImageData
                }
              }
            }
          }
        }
        ... on ContentfulCombinatorSection {
          id
          contentful_id
          combinatorSectionItems {
            ... on ContentfulCta {
              id
              contentful_id
              ctaLabel {
                ctaLabel
              }
              ctaUrl
              ctaType
              ctaTarget
            }
          }
        }
        ... on ContentfulForm {
          contentful_id
          formFields {
            fieldName
            displayLabel
            contentful_id
            fieldContent {
              ... on ContentfulDamAsset {
                contentful_id
                asset {
                  id
                  url
                  mimeType
                  localImage {
                    id
                    publicURL
                    childImageSharp {
                      gatsbyImageData
                    }
                  }
                }
              }
            }
          }
        }
        ... on ContentfulTextSection {
          __typename
          contentful_id
          heading {
            raw
          }
          contentBody {
            raw
            references {
              ... on ContentfulCta {
                id
                contentful_id
                ctaColor
                ctaTarget
                ctaType
                ctaUrl
                ctaLabel {
                  ctaLabel
                }
              }
            }
          }
        }
      }
    }
    contentfulAccessory(
      contentful_id: { eq: $contentful_id }
      node_locale: { eq: $formattedLocale }
    ) {
      __typename
      id
      contentful_id
      isActive
      displayName
      manufacturerName
      description {
        description
      }
      accessory_category {
        categoryName
      }
      type {
        value
      }
      specifications {
        groupName {
          value
        }
        groupItemName {
          value
        }
        value {
          value
        }
        unit {
          value
        }
      }
      heroImageLink
      variants {
        ... on ContentfulAccessoryVariant {
          __typename
          id
          displayName
          accessory {
            urlSlug
          }
          isActive
          vendorSku
          price
          pcoPoints
          productSku
          iqCatalogItemId
          manufacturerModel
          accessoryColour {
            id
            displayName
            hexValue
            assetUrl
          }
          imageLinksList
        }
      }
    }
  }
`;

export default AccessoryDetailsPage;
